import { z } from "zod";
import { PermissionAction, PermissionResource } from "./identity.js";
import { SettingKey } from "./settings.js";

/** Module contract (architecture §6.2): every module, including future ones, is introduced by a manifest. */

const ModuleName = z.string().regex(/^[a-z][a-z0-9-]*$/);

export const ModuleManifest = z.object({
  name: ModuleName,
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  /** Persian label shown in the manager UI (module switches, role designer). */
  label: z.string().min(1),
  dependsOn: z.array(ModuleName).default([]),
  /** Tables must carry the module prefix, e.g. `identity_`. */
  tablePrefix: z.string().regex(/^[a-z][a-z0-9]*_$/),
  permissions: z
    .array(
      z.object({
        resource: PermissionResource,
        label: z.string().min(1),
        actions: z.array(PermissionAction).min(1),
        /**
         * Who may hold it. Customer-context roles can only grant `customer` or `both` resources, so a customer
         * role can never reach staff data (ADR-29 §4).
         */
        audience: z.enum(["staff", "customer", "both"]).default("staff"),
      }),
    )
    .default([]),
  events: z
    .object({ emits: z.array(z.string()).default([]), consumes: z.array(z.string()).default([]) })
    .default({ emits: [], consumes: [] }),
  settings: z.array(z.object({ key: SettingKey, label: z.string().min(1) })).default([]),
  /** Core modules cannot be switched off by the manager. */
  core: z.boolean().default(false),
});
export type ModuleManifest = z.infer<typeof ModuleManifest>;
